/**
 * UI & Observability State (Microservicio de Interfaz y Observabilidad)
 * Gestiona navegación, temas visuales, notificaciones y logs de la API.
 */
import { Injectable, signal, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { I18nService } from '../i18n/i18n.service';
import { ApiCallLog } from '../models/types';
import { subscribeToApiLogs } from '../http/http-client';

export type ActiveTab =
  | 'dashboard'
  | 'squad'
  | 'market'
  | 'competition'
  | 'news'
  | 'club'
  | 'stadium'
  | 'matchengine'
  | 'wallet';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export interface AppNotification {
  message: string;
  type: NotificationType;
}

@Injectable({
  providedIn: 'root',
})
export class UiState {
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);

  readonly activeTab = signal<ActiveTab>('dashboard');
  readonly notification = signal<AppNotification | null>(null);
  readonly notificationsEnabled = signal<boolean>(this.getInitialNotificationsEnabled());
  readonly isDarkMode = signal<boolean>(this.getInitialTheme());
  readonly apiLogs = signal<ApiCallLog[]>([]);

  constructor() {
    subscribeToApiLogs((logs) => {
      this.apiLogs.set(logs);
    });

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const path = event.urlAfterRedirects.split('/')[1]?.split('?')[0] as ActiveTab;
        if (
          path &&
          ['dashboard', 'squad', 'market', 'competition', 'news', 'club', 'stadium', 'matchengine'].includes(
            path
          )
        ) {
          this.activeTab.set(path === 'stadium' ? 'club' : path);
        }
      });

    if (typeof document !== 'undefined') {
      document.documentElement.classList.toggle('dark', this.isDarkMode());
    }
  }

  t(key: string, params?: Record<string, string | number>): string {
    return this.i18n.t(key, params);
  }

  setActiveTab(tab: ActiveTab): void {
    this.activeTab.set(tab);
    if (
      ['dashboard', 'squad', 'market', 'competition', 'news', 'stadium', 'matchengine'].includes(
        tab
      )
    ) {
      this.router.navigate(['/' + tab]);
    }
  }

  showNotification(message: string, type: NotificationType = 'info'): void {
    // Si las notificaciones están desactivadas, solo se muestran las de error y warning
    if (!this.notificationsEnabled() && type !== 'error' && type !== 'warning') {
      return;
    }

    this.notification.set({ message, type });
    setTimeout(() => {
      const cur = this.notification();
      if (cur?.message === message) {
        this.notification.set(null);
      }
    }, 4500);
  }

  toggleNotifications(): void {
    const next = !this.notificationsEnabled();
    this.setNotificationsEnabled(next);
  }

  setNotificationsEnabled(enabled: boolean): void {
    this.notificationsEnabled.set(enabled);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('fm_notifications_enabled', String(enabled));
    }
  }

  private getInitialNotificationsEnabled(): boolean {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('fm_notifications_enabled');
      if (saved !== null) {
        return saved === 'true';
      }
    }
    return false;
  }

  clearNotification(): void {
    this.notification.set(null);
  }

  dismissNotification(): void {
    this.notification.set(null);
  }

  clearApiLogs(): void {
    this.apiLogs.set([]);
  }

  toggleDarkMode(): void {
    const next = !this.isDarkMode();
    this.isDarkMode.set(next);
    if (typeof document !== 'undefined') {
      document.documentElement.classList.toggle('dark', next);
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('fm_theme', next ? 'dark' : 'light');
    }
  }

  private getInitialTheme(): boolean {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('fm_theme');
      if (saved === 'dark') return true;
      if (saved === 'light') return false;
    }
    return false;
  }
}
